import BN from "bignumber.js";

export const objectToQueryString = (obj, prefix) => {
  let str = [];
  let p;
  for (p in obj) {
    if (obj.hasOwnProperty(p)) {
      let k = prefix ? prefix + "[" + p + "]" : p;
      let v = obj[p];
      str.push(
        v !== null && typeof v === "object"
          ? objectToQueryString(v, k)
          : encodeURIComponent(k) + "=" + encodeURIComponent(v)
      );
    }
  }
  return str.join("&");
};

export const queryStringToObject = queryStr => {
  const search = queryStr.startsWith("?") ? queryStr.substring(1) : queryStr;
  if (!search) return {};
  return JSON.parse(
    '{"' +
      decodeURI(search)
        .replace(/"/g, '\\"')
        .replace(/&/g, '","')
        .replace(/=/g, '":"') +
      '"}'
  );
};

export const deriveHdPathStr = (hdPath, accountNum, addressIdx = 0) => {
  return hdPath
    .replace("{accountNum}", accountNum)
    .replace("{addressIdx}", addressIdx);
};

export const normalizeHdPathStr = path => {
  return path.replace(/'|m\//g, "");
};

export const convertHdPathStrToArray = path => {
  return normalizeHdPathStr(path)
    .split("/")
    .map(Number);
};

export const regsiterVuexModules = (store, modules) => {
  for (const module in modules) {
    if (modules.hasOwnProperty(module) && !store.state[module]) {
      store.registerModule(module, modules[module]);
    }
  }
};

export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

export const isLedgerSupported = () => {
  return /Opera|OPR|Chrome/i.test(navigator.userAgent);
};

export const isTrezorSupported = () => {
  return /Opera|OPR|Chrome|Firefox/i.test(navigator.userAgent);
};

export const isImToken = () => {
  return !!window.imToken;
};

export const isMetamask = () => {
  return window.ethereum?.isMetaMask || false;
};

export const isPhantomInstalled = () => {
  return window.phantom?.solana?.isPhantom;
};

export const getWebSocketUrl = (path, query) => {
  const protocol = window.location.protocol.startsWith("https") ? "wss" : "ws";
  const host = path.startsWith("ws")
    ? path
    : `${protocol}://${window.location.host}${path}`;
  const queryStr = objectToQueryString(query);
  return `${host}?${queryStr}`;
};

export const objectToBase64Str = obj => {
  return btoa(JSON.stringify(obj));
};

export const base64StrToObject = str => {
  return JSON.parse(atob(str));
};

export const fetchEthGasPrice = async (apiKey, retry = 1) => {
  try {
    const data = await fetch(
      `https://api.etherscan.io/api?module=gastracker&action=gasoracle&apikey=${apiKey}`
    )
      .then(res => res.json())
      .then(({ result }) => ({
        fast: Number(result.FastGasPrice),
        standard: Number(result.ProposeGasPrice),
        slow: Number(result.SafeGasPrice),
        priority: {
          fast: Number(
            new BN(result.FastGasPrice)
              .minus(result.suggestBaseFee)
              .toFixed(2, BN.ROUND_CEIL)
          ),
          standard: Number(
            new BN(result.ProposeGasPrice)
              .minus(result.suggestBaseFee)
              .toFixed(2, BN.ROUND_CEIL)
          ),
          slow: Number(
            new BN(result.SafeGasPrice)
              .minus(result.suggestBaseFee)
              .toFixed(2, BN.ROUND_CEIL)
          ),
        },
      }));
    return data;
  } catch (error) {
    await sleep(1000);
    if (retry > 0) {
      return fetchEthGasPrice(apiKey, retry - 1);
    }
    throw error;
  }
};

export const fetchEthereumTxStatus = async (
  txhash,
  apiKey = process.env.VUE_APP_ETHERSCAN_API_KEY
) => {
  try {
    const data = await fetch(
      `https://api.etherscan.io/api?module=transaction&action=gettxreceiptstatus&txhash=${txhash}&apikey=${apiKey}`
    )
      .then(res => res.json())
      .then(({ result }) => ({
        confirmed: result.status === "1",
        failed: result.status === "0",
        pending: result.status === "",
      }));
    return data;
  } catch (error) {
    console.log('FETCH_TX_ERROR', error);
    await sleep(5000);
    return fetchEthereumTxStatus(txhash, apiKey);
  }
};

export const saveVaultDepositTxData = data => {
  sessionStorage.setItem(
    `_unagii_latest_deposit_tx_data_${data.vault}_${data.token}_${data.zap ||
      ""}`,
    JSON.stringify(data)
  );
};

export const getVaultDepositData = ({ vault, token, zap }) => {
  try {
    return JSON.parse(
      sessionStorage.getItem(
        `_unagii_latest_deposit_tx_data_${vault}_${token}_${zap || ""}`
      )
    );
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const removeVaultDepositData = ({ vault, token, zap }) => {
  sessionStorage.removeItem(
    `_unagii_latest_deposit_tx_data_${vault}_${token}_${zap || ""}`
  );
};

export const hexlify = value => {
  return "0x" + Number(value).toString(16);
};

export const hexlifyObj = obj => {
  return Object.keys(obj).reduce(
    (acc, cur) => ({ ...acc, [cur]: hexlify(obj[cur]) }),
    {}
  );
};

export const keysToArray = (obj, keyName, valueName) => {
  return Object.keys(obj).map(key => ({
    [keyName]: key,
    [valueName]: obj[key],
  }));
};

export const sleep = async timeout => {
  return new Promise(resolve => {
    setTimeout(resolve, timeout);
  });
};

export const isThemeDark = () => {
  let isDark = false;
  try {
    const theme = localStorage.getItem("__unagii_theme__") || "System";
    switch (theme) {
      case "Dark":
        isDark = true;
        break;
      case "System":
        isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        break;
      default:
        break;
    }
  } catch (error) {
    return false;
  }
  return isDark;
};

export const debouncePromise = (func, wait = 200) => {
  let timestamp;

  const defer = async (...args) => {
    return new Promise(async (resolve, reject) => {
      await sleep(timestamp - new Date().valueOf());
      if (timestamp <= new Date().valueOf()) {
        func(...args)
          .then(resolve)
          .catch(reject);
      }
    });
  };

  const debounced = async (...args) => {
    timestamp = new Date().valueOf() + wait;
    return defer(...args);
  };

  return debounced;
};

export const tryPromise = async (promise, timeout, maxRetries, retries = 1) => {
  try {
    const ret = await promise();
    return ret;
  } catch (error) {
    if (retries === maxRetries) throw error;
    await sleep(timeout);
    return tryPromise(promise, timeout, maxRetries, retries + 1);
  }
};
